import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styles from './AdminDataTable.module.css';

import EditableInput from 'components/common/EditableInput';
import DefaultHeaderCell from 'components/admin/DefaultHeaderCell';
import HighJumpEditHeaderCell from 'components/admin/HighJumpEditHeaderCell';

import { ReactComponent as SearchIcon } from 'assets/images/icon_search.svg';
import { ReactComponent as StarIcon } from 'assets/images/icon_star.svg';
import DiscusHeaderCell from '../DiscusHeaderCell';
import HighJumpHeaderCell from '../HighJumpHeaderCell';
import { getResultHighJumpHeights } from 'config/admin/result';
import CustomCheckbox from 'components/common/CustomCheckbox';
import SearchSelectAthlete from 'components/common/SearchSelectAthlete';
import { ReactComponent as DeleteIcon } from 'assets/images/icon_delete.svg';
import { clubForId } from 'utils/clubFeatures';
import { OTHER_TEAM } from 'utils/relayTeams';
import {
  GROUPING_BASIS,
  groupingBasisOf,
  groupWords,
  formatGroupName,
} from 'utils/groupLabel';

// headerControls: a page's own controls beside the search box, such as the Age
// Groups page's Show hidden tick box. The table's old filter dialog is gone
// (Doug, 2026-10-29): a page that needs a filter puts its own control here.
const AdminDataTable = ({
  data = [],
  isEditable = false,
  isAddedFeatures = true,
  columns: columnsProp,
  headStyle,
  headCellStyle,
  rowStyle,
  tableStyle,
  searchInputPlaceholder,
  headerControls,
  borderType = 'default',
  isShadow = true,
  handleValueChange,
  openConfirmPopup,
  primaryField = 'id',
  handleCheckBoxValueChange,
  handleColDelete,
  handleRowDelete,
}) => {
  // A column flagged groupColumn holds a group number: one athlete's group in
  // the results tables (config/admin/result.js), headed "Age"; or the group a
  // club record is for (config/admin/clubRecord.js), flagged 'group' and
  // headed "Age Group". For a club that groups by year level it is headed
  // "Year" or "Year Group" and shows the year (10 as Y5); it still sorts on
  // the stored number. For an age club the columns are passed through untouched,
  // so nothing about its tables changes. The club is the one in the web
  // address, found in the club list already in memory (no request).
  const { clubId: routeClubId } = useParams();
  const groupingBasis = useSelector((state) =>
    groupingBasisOf(clubForId(state.user, routeClubId))
  );
  const columns = useMemo(() => {
    if (groupingBasis !== GROUPING_BASIS.YEAR_LEVEL || !columnsProp) {
      return columnsProp;
    }
    return columnsProp.map((column) =>
      column.groupColumn
        ? {
            ...column,
            label:
              column.groupColumn === 'group'
                ? groupWords(groupingBasis).group
                : groupWords(groupingBasis).value,
            Cell: ({ value }) => formatGroupName(groupingBasis, value),
          }
        : column
    );
  }, [columnsProp, groupingBasis]);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [highJumpHeights, setHighJumpHeights] = useState([]);
  const [showAthletesSelects, setShowAthletesSelects] = useState({});
  const [showDropDown, setShowDropDown] = useState(false);

  useEffect(() => {
    if (data.length > 0 && data[0].eventType === 'High Jump') {
      setHighJumpHeights(getResultHighJumpHeights(data));
    }
  }, [data]);

  // With no search text every row is shown; otherwise a row is shown when any
  // of its columns contains the text.
  const filteredData = data.filter(
    (row) =>
      !searchQuery ||
      columns.some((column) => {
        const cellValue = column.filterAccessor
          ? row[column.filterAccessor]
          : row[column.accessor];

        // Skip null/undefined/empty values - they don't match
        if (!cellValue) {
          return false;
        }

        // Check if the cell value contains the search query
        return cellValue
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      })
  );

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }
    return 0;
  });

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleShowSelect = (key, value) => {
    setShowDropDown(value);
    setShowAthletesSelects((prevData) => {
      Object.keys(prevData).forEach((k) => (prevData[k] = false));
      return {
        ...prevData,
        [key]: value,
      };
    });
  };

  return (
    <>
      <div
        className={styles.athletesTableWrapper}
        style={{
          boxShadow: isShadow ? undefined : 'unset',
          overflow: showDropDown ? 'visible' : 'auto',
        }}
      >
        <div className={styles.athletesTable} style={tableStyle}>
          {isAddedFeatures ? (
            <div className={styles.athletesTableHeader}>
              {searchInputPlaceholder && (
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    placeholder={searchInputPlaceholder}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <SearchIcon />
                </div>
              )}
              {headerControls}
            </div>
          ) : null}
          <div
            className={`${styles.athletesRows} ${borderType === 'full' ? styles.fullBorder : null}`}
            style={borderType === 'none' ? { border: 'none' } : null}
          >
            <div className={styles.athletesRow} style={headStyle}>
              {columns.map((column, index) => {
                // A high jump height in the edit dialog, found by its name
                // (heightAttempts0, 1, ...): Gender and Age come before the
                // heights, so a height's place does not give its number.
                if (/^heightAttempts\d+$/.test(column.accessor)) {
                  const height = column.accessor;
                  return (
                    <HighJumpEditHeaderCell
                      key={index}
                      value={data[0]?.[height]?.height}
                      column={column}
                      handleColDelete={handleColDelete}
                      inputValueChange={(value) =>
                        handleValueChange(
                          data[0][primaryField],
                          height,
                          value,
                          'height',
                          true
                        )
                      }
                    />
                  );
                }
                switch (column.accessor) {
                  case 'attempts':
                    return (
                      <DiscusHeaderCell
                        key={index}
                        column={column}
                        attemptCount={data[0]?.attempts?.length}
                      />
                    );

                  case 'highJumpAttempts':
                    return (
                      <HighJumpHeaderCell
                        key={index}
                        column={column}
                        heightAttempts={highJumpHeights}
                      />
                    );
                  default:
                    return (
                      <DefaultHeaderCell
                        key={index}
                        column={column}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                        handleColDelete={handleColDelete}
                        headCellStyle={headCellStyle}
                      />
                    );
                }
              })}
            </div>
            {sortedData.map((row, i) => (
              <div key={i} className={styles.athletesRow} style={rowStyle}>
                {columns.map((column, i) => (
                  <div
                    key={i}
                    className={styles.childCell}
                    style={column.style}
                  >
                    {column.checkbox ? (
                      <div className={styles.bodyCheckbox}></div>
                    ) : null}
                    {column.checkboxEnable && (
                      <CustomCheckbox
                        label=""
                        checked={row.isCheck}
                        style={{ margin: 0 }}
                        onChange={() => handleCheckBoxValueChange(row)}
                      />
                    )}
                    {isEditable &&
                    (column.editable || (column.newEditable && row.isNew))
                      ? (() => {
                          switch (column.editType) {
                            case 'attempt':
                              return (
                                <>
                                  <EditableInput
                                    value={
                                      row[column.accessor]?.status !== 'NR'
                                        ? Number(
                                            row[column.accessor]?.distance || 0
                                          ).toFixed(2)
                                        : '0.00'
                                    }
                                    inputValueChange={(value) =>
                                      handleValueChange(
                                        row[primaryField],
                                        column.accessor,
                                        value,
                                        'distance'
                                      )
                                    }
                                  />
                                  <select
                                    className={styles.cellSelect}
                                    value={row[column.accessor]?.status || 'NR'}
                                    onChange={(e) => {
                                      handleValueChange(
                                        row[primaryField],
                                        column.accessor,
                                        e.target.value,
                                        'status'
                                      );
                                      if (e.target.value === 'NR') {
                                        handleValueChange(
                                          row[primaryField],
                                          column.accessor,
                                          0,
                                          'distance'
                                        );
                                      }
                                    }}
                                  >
                                    {column.options?.map((option, index) => (
                                      <option key={index} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </>
                              );
                            case 'highJumpAttempt':
                              // The height by its column's name, not its place (see the heading).
                              return row[column.accessor]?.resultId ? (
                                <>
                                  <select
                                    className={styles.cellSelectHighJump}
                                    value={row[column.accessor]?.attempt1}
                                    onChange={(e) => {
                                      handleValueChange(
                                        row[primaryField],
                                        column.accessor,
                                        e.target.value,
                                        'attempt1'
                                      );
                                    }}
                                  >
                                    {column.options?.map((option, index) => (
                                      <option key={index} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    className={styles.cellSelectHighJump}
                                    value={row[column.accessor]?.attempt2}
                                    onChange={(e) => {
                                      handleValueChange(
                                        row[primaryField],
                                        column.accessor,
                                        e.target.value,
                                        'attempt2'
                                      );
                                    }}
                                  >
                                    {column.options?.map((option, index) => (
                                      <option key={index} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    className={styles.cellSelectHighJump}
                                    value={row[column.accessor]?.attempt3}
                                    onChange={(e) => {
                                      handleValueChange(
                                        row[primaryField],
                                        column.accessor,
                                        e.target.value,
                                        'attempt3'
                                      );
                                    }}
                                  >
                                    {column.options?.map((option, index) => (
                                      <option key={index} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </>
                              ) : (
                                <></>
                              );
                            case 'text':
                              return (
                                <input
                                  className={styles.cellTextInput}
                                  value={row[column.accessor]}
                                  onChange={(e) =>
                                    handleValueChange(
                                      row[primaryField],
                                      column.accessor,
                                      e.target.value
                                    )
                                  }
                                />
                              );
                            case 'select':
                              return (
                                <select
                                  className={styles.cellSelect}
                                  value={row[column.accessor]}
                                  onChange={(e) =>
                                    handleValueChange(
                                      row[primaryField],
                                      column.accessor,
                                      e.target.value
                                    )
                                  }
                                >
                                  <option
                                    style={{ display: 'none' }}
                                    value=""
                                    disabled
                                  ></option>
                                  {column.options?.map((option, index) => (
                                    <option key={index} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              );
                            case 'teamSelect':
                              // A new relay team (utils/relayTeams.js): in a
                              // school the houses and, last, Other team,
                              // which turns the cell into a box for an ad hoc
                              // team's name; in a club the box for the team's
                              // name (no teamChoices).
                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  {!column.teamChoices ||
                                  row.teamChoice === OTHER_TEAM ? (
                                    <input
                                      className={styles.cellTextInput}
                                      maxLength={100}
                                      placeholder="Team name"
                                      value={row.typedTeamName || ''}
                                      onChange={(e) =>
                                        handleValueChange(
                                          row[primaryField],
                                          column.accessor,
                                          { kind: 'typed', name: e.target.value }
                                        )
                                      }
                                    />
                                  ) : (
                                    <select
                                      className={styles.cellSelect}
                                      value={row.teamID || ''}
                                      onChange={(e) =>
                                        handleValueChange(
                                          row[primaryField],
                                          column.accessor,
                                          e.target.value === OTHER_TEAM
                                            ? { kind: 'other' }
                                            : { kind: 'house', teamID: e.target.value }
                                        )
                                      }
                                    >
                                      <option
                                        style={{ display: 'none' }}
                                        value=""
                                        disabled
                                      ></option>
                                      {column.teamChoices.map((choice) => (
                                        <option key={choice.value} value={choice.value}>
                                          {choice.label}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                  <div
                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    onClick={() => handleRowDelete(row)}
                                  >
                                    <DeleteIcon />
                                  </div>
                                </div>
                              );
                            case 'searchSelect':
                              return (
                                <SearchSelectAthlete
                                  groupingBasis={groupingBasis}
                                  columnKey={row[primaryField].toString()}
                                  options={column.options}
                                  handleShowSelect={handleShowSelect}
                                  row={row}
                                  showSelect={
                                    showAthletesSelects[row[primaryField]]
                                  }
                                  handleDeleteIconClick={() =>
                                    row.isNew
                                      ? handleRowDelete(row)
                                      : openConfirmPopup(row)
                                  }
                                  handleOptionChange={(option) =>
                                    handleValueChange(
                                      row[primaryField],
                                      column.accessor,
                                      option
                                    )
                                  }
                                />
                              );
                            default:
                              return <></>;
                          }
                        })()
                      : (() => {
                          // Check if this column should show delete icon for non-editable rows
                          if (
                            column.showDeleteIcon &&
                            openConfirmPopup &&
                            !row.isNew
                          ) {
                            return (
                              <div
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <div>{row[column.accessor]}</div>
                                {/* A saved row's own action, before its delete
                                    icon: a relay team's Runners button. */}
                                {column.rowAction && (
                                  <div style={{ marginLeft: 'auto' }}>
                                    {column.rowAction(row)}
                                  </div>
                                )}
                                <div
                                  style={{
                                    cursor: 'pointer',
                                    marginLeft: '8px',
                                  }}
                                  onClick={() => openConfirmPopup(row)}
                                >
                                  <DeleteIcon />
                                </div>
                              </div>
                            );
                          }

                          // Check if column has custom cell renderer
                          if (column.Cell) {
                            return column.Cell({
                              value: row[column.accessor],
                              row,
                            });
                          }

                          // Regular cell content for all other cases
                          return row[column.accessor];
                        })()}
                    {column.hasStar && row[column.starAccessor] && (
                      <StarIcon style={{ marginLeft: '2px' }} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

AdminDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  headStyle: PropTypes.object,
  headCellStyle: PropTypes.object,
  rowStyle: PropTypes.object,
  tableStyle: PropTypes.object,
  searchInputPlaceholder: PropTypes.string,
  headerControls: PropTypes.node,
  isAddedFeatures: PropTypes.bool,
  borderType: PropTypes.string,
  isShadow: PropTypes.bool,
  isEditable: PropTypes.bool,
  handleValueChange: PropTypes.func,
  openConfirmPopup: PropTypes.func,
  primaryField: PropTypes.string,
  handleCheckBoxValueChange: PropTypes.func,
  handleColDelete: PropTypes.func,
  handleRowDelete: PropTypes.func,
};

export default AdminDataTable;
